import React, { useState } from "react";
import { Dashboard } from "./analyticsState";

interface Props {
  dashboards: Dashboard[];
  activeDashboardId: string | null;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onRename: (id: string, newName: string) => void;
  onTrash: (id: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
  isDark: boolean;
  isCollapsed?: boolean;
  onToggle: () => void;
}

const DashboardSidebar: React.FC<Props> = ({
  dashboards,
  activeDashboardId,
  onSelect,
  onCreate,
  onRename,
  onTrash,
  onRestore,
  onDelete,
  isDark,
  isCollapsed,
  onToggle,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const activeDashboards = dashboards.filter((d) => !d.isTrashed).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const trashedDashboards = dashboards.filter((d) => d.isTrashed).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const handleCreate = () => {
    onCreate(`Dashboard ${dashboards.length + 1}`);
  };

  const startRename = (d: Dashboard) => {
    setEditingId(d.id);
    setEditName(d.name);
  };

  const saveRename = (id: string) => {
    if (editName.trim()) {
      onRename(id, editName.trim());
    }
    setEditingId(null);
  };

  return (
    <div className={`ca-dashboard-sidebar ${isDark ? "ca-dark" : ""} ${isCollapsed ? "ca-collapsed" : ""}`}>
      <div className="ca-dashboard-sidebar-header" style={{ justifyContent: isCollapsed ? "center" : "space-between" }}>
        {!isCollapsed && <span className="ca-dashboard-sidebar-title">Analytics</span>}
        <button className="ca-btn-icon" onClick={onToggle} title="Toggle Dashboard Sidebar">
          <i className={`fa fa-chevron-${isCollapsed ? 'right' : 'left'}`} />
        </button>
      </div>

      <div className="ca-dashboard-list">
        {!isCollapsed && (
          <div className="ca-dashboard-list-title">Recent Dashboards
            <button className="ca-btn ca-btn-primary ca-btn-sm mx-4" onClick={handleCreate} title="Create New">
              <i className="fa fa-plus" />
            </button>
          </div>
        )}
        {activeDashboards.length === 0 ? (
          <div className="ca-dashboard-empty">No dashboards</div>
        ) : (
          activeDashboards.map((d) => (
            <div
              key={d.id}
              className={`ca-dashboard-item ${d.id === activeDashboardId ? "active" : ""}`}
              onClick={() => onSelect(d.id)}
              title={d.name}
            >
              <i className="fa fa-chart-pie ca-dashboard-icon" style={isCollapsed ? { margin: 0 } : {}} />
              {!isCollapsed && (
                <>
                  {editingId === d.id ? (
                    <input
                      autoFocus
                      className="ca-dashboard-rename-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => saveRename(d.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveRename(d.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="ca-dashboard-name">{d.name}</span>
                  )}
                  <div className="ca-dashboard-actions">
                    <button
                      className="ca-dashboard-action-btn"
                      onClick={(e) => { e.stopPropagation(); startRename(d); }}
                      title="Rename"
                    >
                      <i className="fa fa-pencil-alt" />
                    </button>
                    <button
                      className="ca-dashboard-action-btn delete"
                      onClick={(e) => { e.stopPropagation(); onTrash(d.id); }}
                      title="Move to trash"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <div className="ca-dashboard-list" style={{ marginTop: "1rem" }}>
        {!isCollapsed && <div className="ca-dashboard-list-title">Trash</div>}
        {trashedDashboards.length === 0 ? (
          !isCollapsed && <div className="ca-dashboard-empty">Trash is empty</div>
        ) : (
          trashedDashboards.map((d) => (
            <div
              key={d.id}
              className={`ca-dashboard-item trashed ${d.id === activeDashboardId ? "active" : ""}`}
              onClick={() => onSelect(d.id)}
              title={d.name}
            >
              <i className="fa fa-file-alt ca-dashboard-icon" style={isCollapsed ? { margin: 0 } : {}} />
              {!isCollapsed && (
                <>
                  <span className="ca-dashboard-name">{d.name}</span>
                  <div className="ca-dashboard-actions">
                    <button
                      className="ca-dashboard-action-btn"
                      onClick={(e) => { e.stopPropagation(); onRestore(d.id); }}
                      title="Restore"
                    >
                      <i className="fa fa-undo" />
                    </button>
                    <button
                      className="ca-dashboard-action-btn delete"
                      onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}
                      title="Delete permanently"
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DashboardSidebar;
